import { useCallback, useEffect, useState } from 'react'
import {
  useSelectUserGender,
  useSelectUserZipcode,
  useSelectUserBirthYear,
  useSelectFirstName,
  useSelectUserId,
  useSelectUserLanguage,
  useSelectUserMarket,
  useSelectIsUserSso,
  useSelectUserIsManager,
} from '../../state/selectors'
import { Modal } from '../modals/Modal'
import { BodyLargeMedium, BodyMedium, BodySmall, Title3 } from '../styles/Typography'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { OnClickButton } from '../buttons/onclick/OnClickButton'
import { useSupplementaryDataForm } from './useSupplementaryDataForm'
import { FormTypes, SelectOption } from './types'
import { TextInput } from '../inputs/Text'
import { SelectInput } from '../inputs/Select'
import { ZipCodeInput } from '../inputs/ZipCode'
import { useTranslation } from 'react-i18next'
import { isSupportedLanguage } from '@estercare/ester-shared'
import { isSwedishMarket } from '../../utils/market'
import { useUserStore } from '../../state/store'
import { getLanguageOptions, getDefaultLanguage } from '../../utils/languageOptions'
import { Stack } from '@mui/material'
import { Checkbox } from '../inputs/Checkbox'

export const SupplementaryDataModal = () => {
  const { t, i18n } = useTranslation()
  const { setUser } = useUserStore()
  const [showModal, setShowModal] = useState(false)
  const userId = useSelectUserId()
  const userGender = useSelectUserGender()
  const userZipCode = useSelectUserZipcode()
  const userBirthYear = useSelectUserBirthYear()
  const firstName = useSelectFirstName()
  const userLanguage = useSelectUserLanguage()
  const userMarket = useSelectUserMarket()
  const isSso = useSelectIsUserSso()
  const userIsManager = useSelectUserIsManager()

  const [selectedLanguage, setSelectedLanguage] = useState(getDefaultLanguage(userLanguage, userMarket))
  const isSwedish = isSwedishMarket(userMarket)

  const hasAnyExistingData = !!(userGender || (isSwedish && userZipCode) || userBirthYear || userLanguage)

  const shouldHighlight = useCallback(
    (currentValue: string) => !currentValue && hasAnyExistingData,
    [hasAnyExistingData]
  )

  const { formData, formErrors, handleInputChange, onSubmit } = useSupplementaryDataForm(
    {
      birthYear: userBirthYear?.toString() || '',
      gender: userGender || '',
      zipCode: userZipCode?.toString() || '',
      isManager: String(!!userIsManager),
    },
    userId || null,
    () => setShowModal(false),
    selectedLanguage,
    () => {
      const currentUser = useUserStore.getState().user
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          language: selectedLanguage,
        }
        setUser(updatedUser)
        i18n.changeLanguage(selectedLanguage)
      }
    },
    userMarket
  )

  const genderOptions: SelectOption[] = [
    { value: 'F', label: t('common.gender.female') },
    { value: 'M', label: t('common.gender.male') },
    { value: 'U', label: t('common.gender.preferNotToSay') },
  ]

  const languageOptions = getLanguageOptions(t)

  useEffect(() => {
    if (!userGender || (isSwedish && !userZipCode) || !userBirthYear || !userLanguage) {
      setShowModal(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBirthYear, userGender, userZipCode, isSwedish])

  useEffect(() => {
    if (userLanguage) {
      setSelectedLanguage(userLanguage)
    }
  }, [userLanguage])

  if (!showModal) return null

  return (
    <Modal disableClose>
      <StyledTitle3>{t('common.welcome.greeting', { firstName })}</StyledTitle3>
      <BodyLargeMedium>{t('common.welcome.title')}</BodyLargeMedium>
      <BodySmall>{t('common.welcome.subtitle')}</BodySmall>
      <InputWrapper>
        <TextInput
          backgroundColor={theme.color.beigeLight}
          value={formData.birthYear}
          label={t('common.form.birthYear')}
          placeholder="YYYY"
          maxLength={4}
          onChange={handleInputChange(FormTypes.BIRTH_YEAR)}
          error={formErrors?.find((error) => error.formType === FormTypes.BIRTH_YEAR)?.errorMessage}
          highlight={shouldHighlight(formData.birthYear)}
        />
      </InputWrapper>
      <InputWrapper>
        <SelectInput
          backgroundColor={theme.color.beigeLight}
          label={t('common.form.biologicalGender')}
          options={genderOptions}
          value={formData.gender}
          onChange={handleInputChange(FormTypes.GENDER)}
          placeholder={t('common.form.chooseOption')}
          highlight={shouldHighlight(formData.gender)}
        />
      </InputWrapper>
      {isSwedish && (
        <InputWrapper>
          <ZipCodeInput
            value={formData.zipCode}
            backgroundColor={theme.color.beigeLight}
            onChange={handleInputChange(FormTypes.ZIP_CODE)}
            error={formErrors?.find((error) => error.formType === FormTypes.ZIP_CODE)?.errorMessage}
            highlight={shouldHighlight(formData.zipCode)}
            market={userMarket}
          />
        </InputWrapper>
      )}
      <InputWrapper>
        <SelectInput
          backgroundColor={theme.color.beigeLight}
          label={t('common.form.language')}
          options={languageOptions}
          value={selectedLanguage}
          onChange={(value: string) => {
            if (isSupportedLanguage(value)) {
              setSelectedLanguage(value)
            }
          }}
          placeholder={t('common.form.choose')}
          highlight={shouldHighlight(userLanguage || '')}
        />
      </InputWrapper>
      {isSso && (
        <InputWrapper>
          <Stack direction="row">
            <Checkbox
              onChange={() => handleInputChange(FormTypes.IS_MANAGER)(String(formData.isManager !== 'true'))}
              checked={formData.isManager === 'true'}
              borderColor={theme.color.grey3}
              backgroundColor={theme.color.white}
            />
            <CheckboxTextContainer>
              <BodyMedium>{t('common.welcome.isManager')}</BodyMedium>
            </CheckboxTextContainer>
          </Stack>
        </InputWrapper>
      )}
      <ButtonWrapper>
        <OnClickButton
          disabled={!formData.birthYear || !formData.gender || (isSwedish && !formData.zipCode)}
          buttonColor={theme.color.plumMid}
          text={t('common.form.continue')}
          onClick={onSubmit}
        />
      </ButtonWrapper>
    </Modal>
  )
}

const StyledTitle3 = styled(Title3)`
  && {
    margin-bottom: ${theme.spacing.xsmall}px;
  }
`

const InputWrapper = styled.div`
  margin-top: ${theme.spacing.mediumLarge}px;
`

const ButtonWrapper = styled.div`
  margin-top: ${theme.spacing.mediumLarge}px;
  button {
    width: 100%;
  }
`
const CheckboxTextContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.tiny}px;
  margin-left: ${theme.spacing.small}px;
  text-align: left;
  & a {
    color: ${theme.color.plumMid};
    text-decoration: underline;
  }
  & p {
    color: ${theme.color.grey4};
  }
`
